import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Pencil, Trash2, Languages, FileText, HeartPulse, Globe2, Plus } from "lucide-react";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction, ListViewHeaderAction } from "../../../ui/components/ListView";
import type { ListParams } from "../../../ui/api/listApi.types";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import type { ContextAction } from "../../../ui/components/ContextActions";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useI18n } from "../../lib/i18n";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import * as localizationApi from "../../lib/api/localization";
import type { Language } from "../../lib/api/localization";
import { createLanguagesTableColumns } from "./localizationTableColumns";
import { LanguageFormModal } from "./LanguageFormModal";
import { ListViewLanguageCard } from "./ListViewLanguageCard";
import { LocalizationHealthSection } from "./LocalizationHealthSection";
import { useListImportExport } from "../data-transfer/useListImportExport";
import * as layout from "../organizations/OrganizationEditPage.css";

const SECTION_IDS = {
  languages: "localization-languages",
  health: "localization-health",
} as const;

export function LocalizationListPage() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? getAuthCookie("access_token");
  const { confirm, ConfirmModal } = useConfirmModal();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("localization-list-v2");

  const [refreshKey, setRefreshKey] = React.useState(0);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Language | null>(null);
  const [catalogBadge, setCatalogBadge] = React.useState<React.ReactNode | null>(null);
  const [languages, setLanguages] = React.useState<Language[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const refresh = React.useCallback(() => setRefreshKey((k) => k + 1), []);

  const columns = React.useMemo(() => createLanguagesTableColumns(t), [t]);

  const fetchList = React.useCallback(
    async (params: ListParams) => {
      if (!token) return { data: [] as Language[], total: 0 };
      try {
        const rows = await localizationApi.fetchAdminLanguages(token);
        setLanguages(rows);
        setLoadError(null);
        const filtered = params.search
          ? rows.filter((l) =>
              [l.name, l.code, l.description ?? ""]
                .join(" ")
                .toLowerCase()
                .includes(params.search!.toLowerCase()),
            )
          : rows;
        const start = (params.page - 1) * params.limit;
        return {
          data: filtered.slice(start, start + params.limit),
          total: filtered.length,
        };
      } catch (err: any) {
        setLoadError(err?.message || t("pro.system.localization.error.loadLanguages"));
        setLanguages([]);
        return { data: [] as Language[], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, refreshKey, t],
  );

  const exportPreviewItems = React.useMemo(
    () =>
      languages.map((l) => ({
        id: l.id,
        label: l.name,
        matchKey: l.code,
        imageUrl: l.flagUrl ?? undefined,
      })),
    [languages],
  );

  const {
    headerActions: ieHeaderActions,
    exportContextAction,
    modals: importExportModals,
  } = useListImportExport({
    entity: "languages",
    entityLabel: t("pro.system.localization.languages"),
    enableImport: true,
    enableExport: true,
    totalCount: languages.length,
    selectedIds,
    previewItems: exportPreviewItems,
    defaultFormat: "zip",
    onImported: refresh,
  });

  const openLanguage = React.useCallback((row: Language) => {
    setEditing(row);
    setModalOpen(true);
  }, []);

  const getContextActions = React.useCallback(
    (row: Language): ListViewContextAction[] => {
      const actions: ListViewContextAction[] = [
        {
          id: "edit",
          label: t("shared.common.edit"),
          icon: <Pencil size={14} />,
          bulk: false,
          onClick: () => {
            setEditing(row);
            setModalOpen(true);
          },
        },
      ];
      if (!row.isDefault && row.code !== "en") {
        actions.push({
          id: "delete",
          label: t("shared.common.delete"),
          icon: <Trash2 size={14} />,
          danger: true,
          onClick: async () => {
            if (!token) return;
            const ok = await confirm(
              t("pro.system.localization.confirm.deleteTitle"),
              t("pro.system.localization.confirm.deleteBody", {
                name: row.name,
                code: row.code,
              }),
              t("pro.system.localization.confirm.delete"),
              "danger",
            );
            if (!ok) return;
            try {
              await localizationApi.deleteLanguage(token, row.id);
              refresh();
            } catch (err: any) {
              setLoadError(
                err?.message || t("pro.system.localization.error.deleteLanguage"),
              );
            }
          },
        });
      }
      if (exportContextAction) {
        actions.push({
          ...exportContextAction,
          onClick: (ids) => exportContextAction.onClick(ids),
        });
      }
      return actions;
    },
    [token, confirm, refresh, t, exportContextAction],
  );

  if (!token) {
    return (
      <div style={{ padding: "40px" }}>
        <Callout variant="error">
          {t("pro.system.localization.signInRequired")}
        </Callout>
      </div>
    );
  }

  const contextActions: ContextAction[] = [
    {
      id: "definitions",
      label: t("pro.system.localization.definitions"),
      icon: <FileText size={16} />,
      color: "neutral",
      onClick: () => navigate({ to: "/system/localization/definitions" }),
    },
  ];

  const languageHeaderActions: ListViewHeaderAction[] = [
    {
      id: "create-language",
      label: t("pro.system.localization.create"),
      icon: <Plus size={14} />,
      color: "primary",
      onClick: () => {
        setEditing(null);
        setModalOpen(true);
      },
    },
    ...ieHeaderActions,
  ];

  const sectionLayout = bindSectionLayout([
    {
      id: SECTION_IDS.health,
      label: t("pro.system.localization.health.title"),
      icon: <HeartPulse size={14} />,
    },
    {
      id: SECTION_IDS.languages,
      label: t("pro.system.localization.languages"),
      icon: <Languages size={14} />,
    },
  ]);

  return (
    <>
      <DetailPageLayout
        header={{
          media: <Globe2 size={48} />,
          title: t("pro.system.localization.title"),
          subtitle: false,
        }}
        contextActions={contextActions}
        {...sectionLayout}
      >
        <CollapsibleSection
          id={SECTION_IDS.health}
          title={t("pro.system.localization.health.title")}
          icon={<HeartPulse size={20} />}
          expanded={isExpanded(SECTION_IDS.health)}
          onToggleExpanded={() => toggle(SECTION_IDS.health)}
          statusIndicators={catalogBadge ? [catalogBadge] : undefined}
        >
          <div className={layout.cardInner}>
            <LocalizationHealthSection
              hideChrome
              onCatalogBadge={setCatalogBadge}
              refreshKey={refreshKey}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.languages}
          title={t("pro.system.localization.languages")}
          icon={<Languages size={20} />}
          expanded={isExpanded(SECTION_IDS.languages)}
          onToggleExpanded={() => toggle(SECTION_IDS.languages)}
        >
          <div className={layout.cardInner}>
            {loadError && (
              <div style={{ marginBottom: 16 }}>
                <Callout variant="error">{loadError}</Callout>
              </div>
            )}
            <ListView<Language>
              listId="localization-languages"
              userId={user?.id ?? "anonymous"}
              columns={columns}
              fetchList={fetchList}
              contextKey={String(refreshKey)}
              enabledViews={["stack", "table", "deck"]}
              compact
              deckCardHeight={220}
              searchPlaceholder={t("pro.system.localization.searchPlaceholder")}
              emptyTitle={t("pro.system.localization.emptyTitle")}
              emptySubtitle={t("pro.system.localization.emptySubtitle")}
              defaultSort={{ sort_by: "name", order: "asc" }}
              getContextActions={getContextActions}
              onRowClick={openLanguage}
              enableSelection
              onSelectionChange={setSelectedIds}
              headerActions={languageHeaderActions}
              getRowLabel={(row) => row.name}
              renderCard={(row, ctx) => (
                <ListViewLanguageCard
                  key={row.id}
                  layout="deck"
                  language={row}
                  onClick={ctx.onClick}
                  compact={ctx.compact}
                />
              )}
              renderStackCard={(row, ctx) => (
                <ListViewLanguageCard
                  key={row.id}
                  layout="stack"
                  language={row}
                  onClick={ctx.onClick}
                  compact={ctx.compact}
                />
              )}
            />
          </div>
        </CollapsibleSection>
      </DetailPageLayout>

      <LanguageFormModal
        existing={editing}
        open={modalOpen}
        setOpen={setModalOpen}
        onSaved={(saved) => {
          setEditing(saved);
          refresh();
        }}
      />
      {importExportModals}
      <ConfirmModal />
    </>
  );
}
